import React, { useState } from "react";
import { storage } from "../firebase";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { 
    FaFilePdf, FaDownload, FaEye, FaTimes, FaGraduationCap 
} from 'react-icons/fa';
import styles from "./PYQ_YearWise.module.css";
import pyqMobileImg from "../pages/image/prrepx_pyq0.png";
import pdfpageJpg from "../pages/image/pyq_pdf.png";
import SocialSidebar from "./SocialSideBar";

const PYQ_YearWise = () => {
    const examConfig = {
        adv: { label: "JEE Advanced", years: Array.from({ length: 2025 - 2007 + 1 }, (_, i) => 2025 - i) },
        mains: { label: "JEE Mains", years: Array.from({ length: 2025 - 2005 + 1 }, (_, i) => 2025 - i) },
        cbse12: { label: "CBSE Class 12", years: Array.from({ length: 2026 - 2022 + 1 }, (_, i) => 2026 - i), subjects: ["Mathematics", "Physics", "Chemistry", "Biology", "English"] },
        cbse10: { label: "CBSE Class 10", years: Array.from({ length: 2026 - 2022 + 1 }, (_, i) => 2026 - i), subjects: ["Mathematics Basics", "Mathematics Standard", "Science", "English", "Social Studies"] },
    };

    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTitle, setActiveTitle] = useState("");
    const [downloadProgress, setDownloadProgress] = useState({});
    
    // New state to handle expanding sessions for specific years
    const [expandedYear, setExpandedYear] = useState(null);
    const [activeExam, setActiveExam] = useState('all');

    const toggleYear = (year) => {
        setExpandedYear(expandedYear === year ? null : year);
    };

    const fetchFiles = async (exam, year, session = null, subject = null, label) => {
        setLoading(true);
        setIsModalOpen(true);
        setActiveTitle(`${label} - ${year} ${session || ""}`);
        setMaterials([]);

        let folderPath = `pyq/${exam}/${year}/`;
        if (session) folderPath = `pyq/${exam}/${year}/${session}/`;
        if (subject) folderPath = `pyq/${exam}/${subject}/${year}/`;

        try {
            const listRef = ref(storage, folderPath);
            const res = await listAll(listRef);
            const fetchedFiles = await Promise.all(
                res.items.map(async (item) => {
                    const url = await getDownloadURL(item);
                    return { 
                        id: item.fullPath, 
                        name: item.name.replace('.pdf', '').replaceAll('_', ' '), 
                        url 
                    };
                })
            );
            setMaterials(fetchedFiles);
        } catch (error) {
            console.error("Error fetching files:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = (url, fileName, fileId) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';

        xhr.onprogress = (event) => {
            if (event.lengthComputable) {
                const percent = Math.round((event.loaded / event.total) * 100);
                setDownloadProgress(prev => ({ ...prev, [fileId]: percent }));
            }
        };

        xhr.onload = () => {
            if (xhr.status === 200) {
                const blob = xhr.response;
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `${fileName.trim()}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            }
            setTimeout(() => {
                setDownloadProgress(prev => {
                    const newState = { ...prev };
                    delete newState[fileId];
                    return newState;
                });
            }, 2000);
        };
        xhr.send();
    };

    return (
        <div className={styles.pyqContainer}>
            <SocialSidebar />
            {/* ── Hero (matches StudyMaterial page layout) ── */}
            <header className={styles.hero} style={{ backgroundImage: `url(${pdfpageJpg})` }}>
                <div className={`${styles.heroGlow} ${styles.glow1}`} />
                <div className={`${styles.heroGlow} ${styles.glow2}`} />
                <div className={`${styles.heroGlow} ${styles.glow3}`} />

                {/* Mobile image — shown on mobile, hidden on desktop/tablet */}
                <div className={styles.heroMobileImg}>
                    <img src={pyqMobileImg} alt="PYQ Papers" className={styles.heroMobileSlideImg} />
                    <div className={styles.heroMobileBadge}>📄 Year-Wise PYQ Papers — Free</div>
                </div>

                {/* Center — text content */}
                <div className={styles.heroContent}>
                    <div className={styles.heroBadgePill}>📄 Official Papers · 18+ Years Covered</div>
                    <h1 className={styles.heroHeading}>
                        <span className={styles.heroBlue}>Year-Wise PYQ Papers</span>
                        <br />
                        <span className={styles.heroAccent}>JEE · NEET · Class 10 &amp; 12th</span>
                    </h1>
                    <p className={styles.heroTagline}>
                        View &amp; Download authentic PYQs for <strong>JEE Mains, JEE Advanced, NEET, CBSE Class 10 &amp; 12</strong> — sourced directly from official boards, organized year-wise &amp; subject-wise.
                    </p>
                    
                    <p className={styles.alumniText}>✨ JEE · NEET · CBSE — All Year-Wise & Downlodable</p>
                </div>

                <div className={styles.heroScrollHint}>
                    <span>Scroll to explore</span>
                    <div className={styles.scrollArrow} />
                </div>
            </header>

            {/* ── TopBar ── */}
            <div className={styles.topBar}>
                <div className={styles.topBarInner}>
                    <div className={styles.topBarLeft}>
                        <span className={styles.topBarIcon}>📄</span>
                        <span className={styles.topBarTitle}>PYQ Papers</span>
                    </div>
                    <div className={styles.topBarTabs}>
                        <button
                            className={`${styles.topBarTabBtn} ${activeExam === 'all' ? styles.topBarTabBtnActive : ''}`}
                            onClick={() => { setActiveExam('all'); setExpandedYear(null); }}
                        >
                            All
                        </button>
                        {Object.entries(examConfig).map(([id, config]) => (
                            <button
                                key={id}
                                className={`${styles.topBarTabBtn} ${activeExam === id ? styles.topBarTabBtnActive : ''}`}
                                onClick={() => { setActiveExam(id); setExpandedYear(null); }}
                            >
                                {config.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <main className={styles.pyqBody}>
                {Object.entries(examConfig).filter(([exam]) => activeExam === 'all' || exam === activeExam).map(([exam, config]) => (
                    <div key={exam} className={styles.examSection}>
                        <h2 className={styles.examTitle}>{config.label}</h2>
                        {config.subjects ? (
                            config.subjects.map((subject) => (
                                <div key={subject} className={styles.subjectSection}>
                                    <h3 className={styles.subjectTitle}><strong>{subject.toUpperCase()}</strong></h3>
                                    <div className={styles.yearsGrid}>
                                        {config.years.map((year) => (
                                        <div key={year} className={styles.gridItem}>
                                                <button className={styles.yearBtn} onClick={() => fetchFiles(exam, year, null, subject, config.label)}>
                                                        <span className={styles.yearLabel}>{year}</span>
                                                        <span className={styles.yearHint}>View Papers</span>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className={styles.yearsGrid}>
                                {config.years.map((year) => {
                                    const isSessionYear = year >= 2019 && exam === 'mains';
                                    const isExpanded = expandedYear === year;
                                    
                                    return (
                                        <div key={year} className={`${styles.gridItem} ${isExpanded ? styles.activeYear : ''}`}>
                                            <button 
                                                className={styles.yearBtn}
                                                onClick={() => isSessionYear ? toggleYear(year) : fetchFiles(exam, year, null, null, config.label)}
                                            >
                                                <span className={styles.yearLabel}>{year}</span>
                                                <span className={styles.yearHint}>{isSessionYear ? (isExpanded ? 'Hide Sessions ▲' : 'Sessions ▼') : 'View Papers'}</span>
                                            </button>
                                            
                                            {/* Sessions only show if isExpanded is true */}
                                            {isSessionYear && isExpanded && (
                                                <div className={styles.sessionGrid}>
                                                    <span className={styles.sessionGridTitle}>Choose Session</span>
                                                    {["Session1", "Session2"].map(s => (
                                                        <button key={s} className={styles.sessionBtn} onClick={() => fetchFiles(exam, year, s, null, config.label)}>
                                                            {s}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ))}
            </main>

            {isModalOpen && (
                <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
                    <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>{activeTitle}</h2>
                            <button className={styles.closeBtn} onClick={() => setIsModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className={styles.modalBody}>
                            {loading ? (
                                <div className={styles.loader}>Loading PDFs...</div>
                            ) : (
                                <div className={styles.fileGrid}>
                                    {materials.map((file) => {
                                        const progress = downloadProgress[file.id];
                                        const isDownloading = progress !== undefined;
                                        return (
                                            <div key={file.id} className={styles.fileCard}>
                                                <div className={styles.fileIconBox}>
                                                    <FaFilePdf size={45} />
                                                    {isDownloading && <div className={styles.percentageText}>{progress}%</div>}
                                                </div>
                                                <div className={styles.fileInfo}>
                                                    <h4 className={styles.fileName}>{file.name}</h4>
                                                    <div className={styles.fileActions}>
                                                        <a href={file.url} target="_blank" rel="noreferrer" className={styles.viewBtn}>
                                                            <FaEye /> View
                                                        </a>
                                                        <button 
                                                            className={styles.downloadActionBtn} 
                                                            onClick={() => handleDownload(file.url, file.name, file.id)}
                                                            disabled={isDownloading}
                                                        >
                                                            <FaDownload /> {isDownloading ? '...' : 'Download'}
                                                        </button>
                                                    </div>
                                                </div>
                                                {isDownloading && (
                                                    <div className={styles.progressBarContainer}>
                                                        <div className={styles.progressBarFill} style={{ width: `${progress}%` }}></div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        {/* ── CTA BANNER ── */}
        <section className={styles.ctaBanner}>
            <div className={styles.ctaBannerInner}>
                <h2 className={styles.ctaBannerTitle}>Ready to Start Practice?</h2>
                <p className={styles.ctaBannerSub}>One Subscription, Complete Access Model: 471+ Premium Scheduled Tests, Unlimited Open Zone Tests &amp; 80+ Free PYQ Tests.</p>
                <div className={styles.ctaBannerBtns}>
                    <a href="https://testseries.prrepxpathshala.live/test-series/pyq" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtn}>🎯 Free PYQ Tests</a>
                    <a href="https://testseries.prrepxpathshala.live/test-series/jee-premium" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtnSecondary}>⚡ JEE Premium Tests</a>
                    <a href="https://testseries.prrepxpathshala.live/test-series/neet-premium" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtnSecondary}>🩺 NEET Premium Tests</a>
                </div>
            </div>
        </section>

        {/* ── FOOTER NOTE ── */}
        <div className={styles.footerNote}>
            <p>© {new Date().getFullYear()}-{String(new Date().getFullYear() + 1).slice(-2)} PrrepX Pathshala · Led by 100% IIT/NIT & top medical Alumni.</p>
        </div>

    </div>
    );
};

export default PYQ_YearWise;